import React, { Component, useState, useEffect } from 'react'
import { observer, inject } from 'mobx-react';
import { 
  Button, TextArea, Intent, Spinner
} from "@blueprintjs/core";
import Toast from 'helpers/toaster';
import BookingsSidebar from '../bookings/bookings-sidebar';
import { Helmet } from 'react-helmet';
import { observable } from 'mobx';
import _ from 'lodash';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

// Section panel: title + muted description, then rows of fields.
const Section = ({ title, description, children }) => (
  <section className="booking-config__section">
    <div className="booking-config__section-head">
      <h2 className="booking-config__section-title">{title}</h2>
      { description && <p className="booking-config__section-description">{description}</p> }
    </div>
    <div className="booking-config__fields">{children}</div>
  </section>
)

// One setting: label + optional helper text on the left, control on the right.
const Field = ({ id, label, help, children }) => (
  <div className="booking-config__field">
    <div className="booking-config__label-col">
      { id
        ? <label className="booking-config__label" htmlFor={id}>{label}</label>
        : <span className="booking-config__label">{label}</span>
      }
      { help && <p className="booking-config__help">{help}</p> }
    </div>
    <div className="booking-config__control">{children}</div>
  </div>
)

// Swatch + hex text field for one store value. The swatch writes like the old
// native picker; the text field keeps a local draft and only writes once it is
// a complete #rrggbb, so a half-typed hex never reaches the store.
const ColorInput = ({ id, value, onChange }) => {
  const stored = value === null || value === undefined ? '' : `${value}`;
  const [draft, setDraft] = useState(stored);

  useEffect(() => {
    setDraft(current => (current.toLowerCase() === stored.toLowerCase() ? current : stored));
  }, [stored]);

  return (
    <div className="booking-config__color">
      <input
        type="color"
        className="booking-config__swatch"
        aria-label="Pick a color"
        value={HEX_COLOR.test(stored) ? stored : '#000000'}
        onChange={event => onChange(event.target.value)}
      />
      <input
        id={id}
        type="text"
        className="bp3-input booking-config__hex"
        placeholder="#000000"
        spellCheck={false}
        maxLength={7}
        value={draft}
        onChange={event => {
          const next = event.target.value.trim();
          setDraft(next);
          if (HEX_COLOR.test(next)) onChange(next.toLowerCase());
        }}
        onBlur={() => setDraft(stored)}
      />
    </div>
  )
}

@inject('preferenceStore','viewStore','store') @observer
class BookingConfiguration extends Component {

  componentDidMount() {
    let {
      preferenceStore
    } = this.props;
    preferenceStore.loadTenant()
  }

  updateBookingConfiguration() {
    let {
      preferenceStore: {
        tenant: {
          object
        }
      }
    } = this.props;
    

    if(object.attributes.heading_text_for_service_type === "" || object.attributes.heading_text_for_service_type === null) {
      this.props.preferenceStore.tenant.object.attributes.heading_text_for_service_type = 'Choose Service Type';
    }

    if(object.attributes.another_heading_text_for_service === "" || object.attributes.another_heading_text_for_service === null) {
      this.props.preferenceStore.tenant.object.attributes.another_heading_text_for_service = 'Choose Your Service Type';
    }
   
    this.props.preferenceStore.updateTenant(object)
      .then(this.onSuccessfulUpdateConfig.bind(this))
      .catch(this.onFailUpdateConfig.bind(this))
  }

  onSuccessfulUpdateConfig(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Booking Configuration updated successfully.', intent: Intent.SUCCESS })
  }

  onFailUpdateConfig(error) {
    Toast.show({ icon: 'warning-sign', message: 'Problem in updating Booking Configuration!', intent: Intent.DANGER })
  }

  handleChange(event) {
    let {
      preferenceStore: {
        tenant,
        tenant: {
          object
        }
      }
    } = this.props;
    object.attributes.enable_booking_price_display = event.target.value;
  };

  handleChangeVisitUs(event) {
    let {
      preferenceStore: {
        tenant,
        tenant: {
          object
        }
      }
    } = this.props;
    object.attributes.enable_visit_us = event.target.value;
  };

  handleChangeOftenVisitUs(event) {
    let {
      preferenceStore: {
        tenant,
        tenant: {
          object
        }
      }
    } = this.props;
    object.attributes.enable_often_visit_us = event.target.value;
  };

  handleChangePaymentMethod(event) {
    let {
      preferenceStore: {
        tenant,
        tenant: {
          object
        }
      }
    } = this.props;
    object.attributes.payment_method = event.target.value;
  };

  handleChangeLimitAddress(event) {
    let {
      preferenceStore: {
        tenant,
        tenant: {
          object
        }
      }
    } = this.props;
    object.attributes.limit_address = event.target.value;
  };

  render() {
    let {
      preferenceStore: {
        tenant,
        tenant: {
          object
        }
      }
    } = this.props;

    const title = `Booking Configuration | FieldNexus`
    return (

      <div className="row no-gutters company-page-layput">
        <div className="col-lg-2 sidebar-responsive">
            <BookingsSidebar />
            <Helmet>
              <title>{title}</title>
            </Helmet>
        </div>

        <div className="col-lg-10">
          <div className="booking-config">
            <header className="booking-config__header">
              <div className="booking-config__heading">
                <h1 className="booking-config__title">Booking configuration</h1>
                <p className="booking-config__description">
                  Control how your customer-facing booking form looks, what it asks and what happens after someone books.
                </p>
              </div>
              { object &&
                <Button
                  className="booking-config__save"
                  intent={Intent.PRIMARY}
                  loading={tenant.isLoading}
                  onClick={this.updateBookingConfiguration.bind(this)}
                  icon="floppy-disk"
                  text="Save Details"
                />
              }
            </header>

            {
              tenant.isLoading && !object &&
                <div className="booking-config__loading"><Spinner /></div>
            }

            { object &&
              <div className="booking-config__sections">

                <Section
                  title="Appearance"
                  description="How the booking form looks on your website."
                >
                  <Field id="bc-view-style" label="Display style" help="Show the form as a full page or as a widget embedded in your site.">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-view-style"
                        defaultValue={object.attributes.booking_view_style}
                        onChange={(event) => { object.attributes.booking_view_style = event.target.value }}
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='page'>Page</option>
                        <option value='widget'>Widget</option>
                      </select>
                    </div>
                  </Field>
                  <Field id="bc-form-color" label="Form color" help="Used for buttons and highlights.">
                    <ColorInput
                      id="bc-form-color"
                      value={object.attributes.booking_form_color}
                      onChange={value => {
                        object.attributes.booking_form_color = value;
                      }}
                    />
                  </Field>
                  <Field id="bc-form-bgcolor" label="Background color">
                    <ColorInput
                      id="bc-form-bgcolor"
                      value={object.attributes.booking_form_bgcolor}
                      onChange={value => {
                        object.attributes.booking_form_bgcolor = value;
                      }}
                    />
                  </Field>
                  <Field id="bc-display-service-type" label="Service types shown as" help="Show each service type as a text label or as its icon.">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-display-service-type"
                        defaultValue={object.attributes.display_service_type}
                        onChange={(event) => { object.attributes.display_service_type = event.target.value }}
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='Text'>Text</option>
                        <option value='Icon'>Icon</option>
                      </select>
                    </div>
                  </Field>
                </Section>

                <Section
                  title="Booking page text"
                  description="Headings and labels your customers read on the form."
                >
                  <Field id="bc-page-title" label="Page title">
                    <input
                      id="bc-page-title"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Booking Page Title"
                      defaultValue={object.attributes.booking_page_title}
                      onChange={event => {
                        object.attributes.booking_page_title = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-page-subtitle" label="Page subtitle">
                    <input
                      id="bc-page-subtitle"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Booking Page Subtitle"
                      defaultValue={object.attributes.booking_page_sub_title}
                      onChange={event => {
                        object.attributes.booking_page_sub_title = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-service-heading" label="Service type heading" help="Left empty, this shows “Choose Service Type”.">
                    <input
                      id="bc-service-heading"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Service Type Heading"
                      defaultValue={object.attributes.heading_text_for_service_type || `Choose Service Type`}
                      onChange={event => {
                        object.attributes.heading_text_for_service_type = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-service-heading-alt" label="Second service type heading" help="Left empty, this shows “Choose Your Service Type”.">
                    <input
                      id="bc-service-heading-alt"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Service Type Another Heading"
                      defaultValue={object.attributes.another_heading_text_for_service || `Choose Your Service Type`}
                      onChange={event => {
                        object.attributes.another_heading_text_for_service = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-cta-title" label="Button label" help="Text on the form's main call-to-action button.">
                    <input
                      id="bc-cta-title"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="CTA Button Title"
                      defaultValue={object.attributes.cta_title}
                      onChange={event => {
                        object.attributes.cta_title = event.target.value;
                      }}
                    />
                  </Field>
                </Section>

                <Section
                  title="Pricing and payments"
                  description="Whether customers see prices and can pay when they book."
                >
                  <Field id="bc-price-display" label="Show prices to customers">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-price-display"
                        defaultValue={object.attributes.enable_booking_price_display}
                        onChange={(event) => {this.handleChange(event)} }
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='true'>Yes</option>
                        <option value='false'>No</option>
                      </select>
                    </div>
                  </Field>
                  { object.attributes.enable_booking_price_display === "true" || object.attributes.enable_booking_price_display === true ?
                    <Field id="bc-pricing-parameter" label="Use pricing parameters" help="Only applies when prices are shown.">
                      <div className="bp3-select bp3-fill">
                        <select
                          id="bc-pricing-parameter"
                          defaultValue={object.attributes.enable_booking_pricing_parameter}
                          onChange={(event) => { object.attributes.enable_booking_pricing_parameter = event.target.value }}
                        >
                          <option value='' disabled>Select option..</option>
                          <option value='true'>Yes</option>
                          <option value='false'>No</option>
                        </select>
                      </div>
                    </Field>
                    :
                    ''
                  }
                  <Field id="bc-pricing-title" label="Pricing title">
                    <input
                      id="bc-pricing-title"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Pricing Title"
                      defaultValue={object.attributes.pricing_title}
                      onChange={event => {
                        object.attributes.pricing_title = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-payment-method" label="Take payment on booking" help="Ask customers for a payment method when they book.">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-payment-method"
                        defaultValue={object.attributes.payment_method}
                        onChange={(event) => {this.handleChangePaymentMethod(event)} }
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='true'>Yes</option>
                        <option value='false'>No</option>
                      </select>
                    </div>
                  </Field>
                </Section>

                <Section
                  title="Questions for customers"
                  description="Extra questions on the booking form."
                >
                  <Field id="bc-visit-us" label="Ask when to visit" help="“When would you like us to visit?”">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-visit-us"
                        defaultValue={object.attributes.enable_visit_us}
                        onChange={(event) => {this.handleChangeVisitUs(event)} }
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='true'>Yes</option>
                        <option value='false'>No</option>
                      </select>
                    </div>
                  </Field>
                  <Field id="bc-often-visit-us" label="Ask how often to visit" help="“How often you want us to visit?”">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-often-visit-us"
                        defaultValue={object.attributes.enable_often_visit_us}
                        onChange={(event) => {this.handleChangeOftenVisitUs(event)} }
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='true'>Yes</option>
                        <option value='false'>No</option>
                      </select>
                    </div>
                  </Field>
                  <Field id="bc-start-project" label="How soon would you like to start this project?">
                    <TextArea
                      id="bc-start-project"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="How soon would you like to start this project?"
                      defaultValue={object.attributes.start_project_expected_period}
                      onChange={event => {
                        object.attributes.start_project_expected_period = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-contact-way" label="What is the best way for us to contact you?">
                    <TextArea
                      id="bc-contact-way"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="What is the best way for us to contact you?"
                      defaultValue={object.attributes.contact_way}
                      onChange={event => {
                        object.attributes.contact_way = event.target.value;
                      }}
                    />
                  </Field>
                </Section>

                <Section
                  title="Service area"
                  description="Only accept bookings for addresses inside an area you choose."
                >
                  <Field id="bc-limit-address" label="Limit the addresses">
                    <div className="bp3-select bp3-fill">
                      <select
                        id="bc-limit-address"
                        defaultValue={object.attributes.limit_address}
                        onChange={(event) => {this.handleChangeLimitAddress(event)} }
                      >
                        <option value='' disabled>Select option..</option>
                        <option value='true'>Yes</option>
                        <option value='false'>No</option>
                      </select>
                    </div>
                  </Field>
                  { object.attributes.limit_address === "true" || object.attributes.limit_address === true ?
                    <Field label="Boundaries" help="The north, south, east and west edges of the area you serve.">
                      <div className="booking-config__bounds">
                        <label className="booking-config__bound">
                          <span className="booking-config__bound-label">North</span>
                          <input
                            type="text"
                            className="bp3-input bp3-fill"
                            placeholder="North"
                            defaultValue={object.attributes.north}
                            onChange={event => {
                              object.attributes.north = event.target.value;
                            }}
                          />
                        </label>
                        <label className="booking-config__bound">
                          <span className="booking-config__bound-label">South</span>
                          <input
                            type="text"
                            className="bp3-input bp3-fill"
                            placeholder="South"
                            defaultValue={object.attributes.south}
                            onChange={event => {
                              object.attributes.south = event.target.value;
                            }}
                          />
                        </label>
                        <label className="booking-config__bound">
                          <span className="booking-config__bound-label">East</span>
                          <input
                            type="text"
                            className="bp3-input bp3-fill"
                            placeholder="East"
                            defaultValue={object.attributes.east}
                            onChange={event => {
                              object.attributes.east = event.target.value;
                            }}
                          />
                        </label>
                        <label className="booking-config__bound">
                          <span className="booking-config__bound-label">West</span>
                          <input
                            type="text"
                            className="bp3-input bp3-fill"
                            placeholder="West"
                            defaultValue={object.attributes.west}
                            onChange={event => {
                              object.attributes.west = event.target.value;
                            }}
                          />
                        </label>
                      </div>
                    </Field>
                    :''
                  }
                </Section>

                <Section
                  title="After booking"
                  description="What customers see and receive once their booking is sent."
                >
                  <Field id="bc-confirmation-title" label="Confirmation title">
                    <input
                      id="bc-confirmation-title"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Confirmation Message Title"
                      defaultValue={object.attributes.booking_confirmation_message_title || `Booking Successful`}
                      onChange={event => {
                        object.attributes.booking_confirmation_message_title = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-confirmation-message" label="Confirmation message">
                    <input
                      id="bc-confirmation-message"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Confirmation Message"
                      defaultValue={object.attributes.booking_confirmation_message || `Your booking is recorded successfully.`}
                      onChange={event => {
                        object.attributes.booking_confirmation_message = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-reply-to" label="Reply-to email" help="Where customer replies to the booking email go.">
                    <input
                      id="bc-reply-to"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Ex. john@gmail.com"
                      defaultValue={object.attributes.booking_reply_to}
                      onChange={event => {
                        object.attributes.booking_reply_to = event.target.value;
                      }}
                    />
                  </Field>
                  <Field id="bc-email-body" label="Customer email body" help="Sent to the customer after they book.">
                    <TextArea
                      id="bc-email-body"
                      type="text"
                      className="bp3-input bp3-fill"
                      placeholder="Booking customer email body"
                      defaultValue={object.attributes.booking_customer_email_body}
                      onChange={event => {
                        object.attributes.booking_customer_email_body = event.target.value;
                      }}
                    />
                  </Field>
                </Section>

              </div>
            }
          </div>
        </div>

      </div>
    );
  }

};
export default BookingConfiguration;